class CatalogueAutoScroll extends HTMLElement {
	constructor() {
		super();
		this.grid = null;
		this.animationFrame = null;
		this.targetScrollSpeed = 0;
		this.currentScrollSpeed = 0;
		this.maxScrollSpeed = 4;
		this.smoothFactor = 0.12;
		this.deadZone = 0.15; // Zone centrale où il n'y a pas de scroll (15% de chaque côté du centre)
		this.isDesktop = window.innerWidth >= 1000;
		this.isActive = false;

		this.init();
	}

	init() {
		this.grid = this.querySelector('[data-catalogue-grid]');
		if (!this.grid) return;

		// Center scroll on mobile
		if (!this.isDesktop) {
			this.centerScrollOnMobile();
		} else {
			this.bindEvents();
		}
	}

	centerScrollOnMobile() {
		// Wait for next frame to ensure layout is ready
		requestAnimationFrame(() => {
			if (!this.grid) return;

			const scrollWidth = this.grid.scrollWidth;
			const clientWidth = this.grid.clientWidth;
			const scrollLeft = (scrollWidth - clientWidth) / 2;

			this.grid.scrollLeft = scrollLeft;
		});
	}

	bindEvents() {
		// Only bind events on desktop
		if (!this.isDesktop) return;

		this.addEventListener('mouseenter', (e) => this.onMouseEnter(e));
		this.addEventListener('mousemove', (e) => this.onMouseMove(e));
		this.addEventListener('mouseleave', () => this.onMouseLeave());

		// Handle window resize
		window.addEventListener('resize', () => {
			const wasDesktop = this.isDesktop;
			this.isDesktop = window.innerWidth >= 1000;

			// If switching from desktop to mobile, stop scroll
			if (wasDesktop && !this.isDesktop) {
				this.stopScroll();
			}
			// If switching from mobile to desktop, init
			else if (!wasDesktop && this.isDesktop) {
				this.init();
			}
		});
	}

	onMouseEnter(event) {
		// Only work on desktop
		if (!this.isDesktop) return;
		this.isActive = true;
		this.startScrollAnimation();
	}

	onMouseMove(event) {
		// Only work on desktop
		if (!this.isDesktop || !this.isActive) return;

		const rect = this.getBoundingClientRect();

		// Position relative de la souris (0 = gauche, 1 = droite)
		const relativeX = (event.clientX - rect.left) / rect.width;

		// Convertir en position centrée (-1 = gauche, 0 = centre, 1 = droite)
		const centeredPosition = (relativeX - 0.5) * 2;

		// Appliquer la zone morte au centre
		let intensity = 0;

		if (Math.abs(centeredPosition) > this.deadZone) {
			// Calculer l'intensité en dehors de la zone morte
			const absPosition = Math.abs(centeredPosition);
			const normalizedIntensity = (absPosition - this.deadZone) / (1 - this.deadZone);

			// Appliquer le signe (gauche = négatif, droite = positif)
			intensity = Math.sign(centeredPosition) * normalizedIntensity;
		}

		// Appliquer une courbe cubic-bezier pour un mouvement ultra-smooth
		const easedIntensity = this.cubicBezier(Math.abs(intensity)) * Math.sign(intensity);

		// Calculer la vitesse cible
		this.targetScrollSpeed = easedIntensity * this.maxScrollSpeed;
	}

	onMouseLeave() {
		this.isActive = false;
		this.stopScroll();
	}

	// Cubic Bezier implementation (0.25, 0.46, 0.45, 0.94) - smooth ease-out
	cubicBezier(t) {
		const x1 = 0.25,
			y1 = 0.46,
			x2 = 0.45,
			y2 = 0.94;

		// Approximation de cubic-bezier avec fonction polynomial
		// Pour une courbe plus smooth et naturelle
		return 3 * Math.pow(1 - t, 2) * t * y1 + 3 * (1 - t) * Math.pow(t, 2) * y2 + Math.pow(t, 3);
	}

	// Linear interpolation
	lerp(start, end, factor) {
		return start + (end - start) * factor;
	}

	startScrollAnimation() {
		// Only on desktop
		if (this.animationFrame || !this.isDesktop) return;

		const animate = () => {
			if (!this.isActive || !this.isDesktop) {
				// Si on n'est plus actif ou plus sur desktop, décelerer vers 0
				this.targetScrollSpeed = 0;
			}

			// Interpolation smooth vers la vitesse cible
			this.currentScrollSpeed = this.lerp(
				this.currentScrollSpeed,
				this.targetScrollSpeed,
				this.smoothFactor,
			);

			// Scroll seulement si la vitesse est significative et qu'on est sur desktop
			if (Math.abs(this.currentScrollSpeed) > 0.01 && this.isDesktop) {
				this.grid.scrollBy({
					left: this.currentScrollSpeed,
					behavior: 'auto',
				});
			}

			// Continuer l'animation tant qu'on a une vitesse ou qu'on est actif
			if ((this.isActive || Math.abs(this.currentScrollSpeed) > 0.01) && this.isDesktop) {
				this.animationFrame = requestAnimationFrame(animate);
			} else {
				this.animationFrame = null;
			}
		};

		animate();
	}

	stopScroll() {
		this.isActive = false;
		this.targetScrollSpeed = 0;

		// L'animation va continuer jusqu'à ce que la vitesse atteigne près de 0
	}

	disconnectedCallback() {
		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
			this.animationFrame = null;
		}
		this.currentScrollSpeed = 0;
		this.targetScrollSpeed = 0;
		this.isActive = false;
	}
}

// Register the custom element
customElements.get('catalogue-auto-scroll') ||
	customElements.define('catalogue-auto-scroll', CatalogueAutoScroll);
